import { maybe, rd, type Maybe, type RemoteData } from '@passionware/monads';
import type {
  EnterpriseTeam,
  EnterpriseWorkspace
} from 'v5/api/enterprise/enterprise.api';
import type {
  AppScope,
  ResolvedAppScope
} from 'v5/core/app-scope/appScope.types';
import type { EnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

function resolveWorkspaceScope(
  organizationId: string,
  workspaceSlug: string,
  workspaceRd: RemoteData<Maybe<EnterpriseWorkspace>>
): RemoteData<ResolvedAppScope> {
  return rd.map(workspaceRd, ws => {
    if (maybe.isAbsent(ws)) {
      throw new Error(`Workspace "${workspaceSlug}" not found`);
    }
    return {
      level: 'workspace' as const,
      organizationId,
      workspaceId: ws.id,
      workspaceSlug: ws.slug
    };
  });
}

function resolveTeamScope(
  organizationId: string,
  workspaceSlug: string,
  teamSlug: string,
  workspaceRd: RemoteData<Maybe<EnterpriseWorkspace>>,
  teamRd: RemoteData<Maybe<EnterpriseTeam>>
): RemoteData<ResolvedAppScope> {
  return rd.map(
    rd.combine({ ws: workspaceRd, team: teamRd }),
    ({ ws, team }) => {
      if (maybe.isAbsent(ws)) {
        throw new Error(`Workspace "${workspaceSlug}" not found`);
      }
      if (maybe.isAbsent(team)) {
        throw new Error(`Team "${teamSlug}" not found`);
      }
      return {
        level: 'team' as const,
        organizationId,
        workspaceId: ws.id,
        workspaceSlug: ws.slug,
        teamId: team.id,
        teamSlug: team.slug
      };
    }
  );
}

export function useResolvedAppScope(
  slugScope: Maybe<AppScope>,
  enterpriseService: EnterpriseService
): RemoteData<ResolvedAppScope> {
  const scope = maybe.isPresent(slugScope)
    ? maybe.getOrThrow(slugScope)
    : undefined;

  const workspaceRd = enterpriseService.useWorkspaceBySlug(
    scope != null && scope.level !== 'organization'
      ? maybe.of(scope.organizationId)
      : maybe.ofAbsent(),
    scope != null && (scope.level === 'workspace' || scope.level === 'team')
      ? maybe.of(scope.workspaceSlug)
      : maybe.ofAbsent()
  );

  const teamRd = enterpriseService.useTeamBySlug(
    scope?.level === 'team' ? maybe.of(scope.organizationId) : maybe.ofAbsent(),
    scope?.level === 'team' ? maybe.of(scope.workspaceSlug) : maybe.ofAbsent(),
    scope?.level === 'team' ? maybe.of(scope.teamSlug) : maybe.ofAbsent()
  );

  if (scope == null) {
    return rd.ofIdle();
  }

  if (scope.level === 'organization') {
    return rd.of({
      level: 'organization',
      organizationId: scope.organizationId
    });
  }

  if (scope.level === 'workspace') {
    return resolveWorkspaceScope(
      scope.organizationId,
      scope.workspaceSlug,
      workspaceRd
    );
  }

  return resolveTeamScope(
    scope.organizationId,
    scope.workspaceSlug,
    scope.teamSlug,
    workspaceRd,
    teamRd
  );
}
